import React, { useState, useEffect, useRef, useMemo } from "react"
import { useQuery } from "react-query"
import { Box, Link as Clink, Grid, Button, Flex } from "@chakra-ui/react"
import _ from "lodash"
import {
  getAccount,
  getAllAccounts,
  getTradeHistory,
} from "../../../services/crm.service"
import { AgGridReact } from "ag-grid-react"
import { Link } from "react-router-dom"
import DateRangePicker from "../../composites/date-range-picker"
import { formatDate } from "../../../utils"
import { AiOutlineSearch, AiOutlineDownload } from "react-icons/ai"

// const captions = [
//   "Time",
//   "Login",
//   "Buy/Sell",
//   "Entry",
//   "Deal",
//   "Symbol",
//   "Volume",
//   "Profit",
//   "Comment",
// ]

const captions = [
  "Deal",
  "Order",
  "Position",
  "Login",
  "Name",
  "Time",
  "Type",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S / L",
  "T / P",
  "Reason",
  "Commission",
  "Fee",
  "Profit",
  "Dealer",
  "Comment",
]

// {"positionId":1619571,"dealId":1051988,"orderId":1811891,"volume":5000,"symbol":"CRUDE_XI","price":6462,"rateMargin":6462,
// "loginId":25952,"sL":0,"tp":0,"marketAsk":6462,"marketBid":6460,"action":0,"entry":0}

const getAction = (_type: number) => {
  switch (_type) {
    case 0:
      return "BUY"

    case 1:
      return "SELL"

    case 2:
      return "DEAL_BALANCE"

    case 3:
      return "DEAL_CREDIT"

    default:
      return _type
  }
}

const AccountHistoryPage = ({ login }: { login: number }) => {
  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "Position",
      field: "positionId",
    },
    {
      headerName: "Deal",
      field: "dealId",
    },
    {
      headerName: "Order",
      field: "orderId",
    },
    { headerName: "Volume", field: "volume" },
    { headerName: "Symbol", field: "symbol" },
    { headerName: "Price", field: "price" },
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params: any) => {
        return <>{params?.value === 0 ? "BUY" : getAction(params?.value)}</>
      },
    },
    {
      headerName: "entry",
      field: "entry",
      cellRenderer: (params: any) => {
        return <>{params?.value === 0 ? "IN" : "OUT"}</>
      },
    },
  ])

  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([new Date(), new Date()])

  const [finalState, setFinalState] = useState({
    startDate: startDate,
    endDate: endDate,
  })

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
    refetch,
  } = useQuery([`account-` + login, columnDefs], () => getAccount(login), {
    refetchInterval: 4000,
  })

  const historyQuery = useQuery(
    [`trades-history` + login, columnDefs, finalState],
    () =>
      getTradeHistory(
        login,
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : ""
      )
  )

  const gridRef = useRef<any>() // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState() // Set rowData to Array of Objects, one Object per Row

  const updateFinalState = () => {
    let someDate = new Date(endDate ? endDate : startDate!)

    someDate.setDate(someDate.getDate() + 1)

    setFinalState({
      startDate,
      endDate: someDate,
    })
  }

  useEffect(() => {
    updateFinalState()
  }, [])
  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(
    () => ({
      // make every column editable
      editable: false,
      // make every column use 'text' filter by default
      filter: "agTextColumnFilter",
      // enable floating filters by default
      floatingFilter: true,
      // make columns resizable
      resizable: true,
      // disable cell data types
      cellDataType: false,
      sortable: true,
    }),
    []
  )

  const account = data?.data?.data ?? null
  const history =
    historyQuery?.isError || historyQuery.isLoading
      ? []
      : historyQuery?.data?.data?.data ?? []

  return (
    <>
      <Flex align="center" justify="space-between">
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box pt={[0, "35px"]} pr={4} textAlign="right">
          <Button
            colorScheme="blue"
            onClick={() => {
              updateFinalState()
            }}
            isLoading={historyQuery?.isLoading}
          >
            <Box as={AiOutlineSearch} mr={2} />
            Search
          </Button>
        </Box>
      </Flex>

      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box></Box>
        <Box></Box>
        <Box></Box>
      </Grid>

      <div className="ag-grid-container">
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "calc(100vh - 200px)" }}
        >
          <AgGridReact
            suppressScrollOnNewData={true}
            ref={gridRef} // Ref for accessing Grid's API
            rowData={history ?? []} // Row Data for Rows
            columnDefs={columnDefs} // Column Defs for Columns
            defaultColDef={defaultColDef} // Default Column Properties
            animateRows={true} // Optional - set to 'true' to have rows animate when sorted
            overlayNoRowsTemplate={
              isLoading ? "Loading data..." : "No data found"
            }
          />
        </div>
      </div>
    </>
  )
}

export default AccountHistoryPage
